import axios from 'axios';

// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
const API_BASE_URL = 'https://findesk.rmc2.com.ph/api';

function buildClientRequestId() {
  return `findesk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getRequestDuration(metadata) {
  if (!metadata?.startedAt) {
    return null;
  }

  return Date.now() - metadata.startedAt;
}

function appendRequestId(message, requestId) {
  if (!requestId) {
    return message;
  }

  return `${message} Request ID: ${requestId}`;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
});

apiClient.interceptors.request.use((config) => {
  const requestId = config.headers?.['X-Client-Request-Id'] || buildClientRequestId();

  config.headers = {
    ...config.headers,
    'X-Client-Request-Id': requestId,
  };

  config.metadata = {
    startedAt: Date.now(),
    requestId,
  };

  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    const durationMs = getRequestDuration(response.config?.metadata);

    console.info('[Findesk API]', {
      method: response.config?.method,
      url: response.config?.url,
      status: response.status,
      durationMs,
      requestId:
        response.data?.debug?.request_id || response.config?.metadata?.requestId || null,
    });

    return response;
  },
  (error) => {
    const durationMs = getRequestDuration(error.config?.metadata);

    console.error('[Findesk API]', {
      method: error.config?.method,
      url: error.config?.url,
      status: error.response?.status || null,
      durationMs,
      code: error.code || null,
      requestId:
        error.response?.data?.debug?.request_id || error.config?.metadata?.requestId || null,
      message: error.response?.data?.message || error.message,
      responseData: error.response?.data || null,
    });

    return Promise.reject(error);
  }
);

export function getStoredAuth() {
  return {
    accessToken: localStorage.getItem('findesk_access_token') || '',
    refreshToken: localStorage.getItem('findesk_refresh_token') || '',
  };
}

export function storeAuth(accessToken, refreshToken) {
  localStorage.setItem('findesk_access_token', accessToken || '');
  localStorage.setItem('findesk_refresh_token', refreshToken || '');
}

export function clearAuth() {
  localStorage.removeItem('findesk_access_token');
  localStorage.removeItem('findesk_refresh_token');
}

export function resolveApiErrorMessage(error, fallbackMessage) {
  const requestId = error?.response?.data?.debug?.request_id || error?.config?.metadata?.requestId;

  if (error?.code === 'ECONNABORTED') {
    return appendRequestId(
      'Request timed out after 20 seconds. Check the server, PHP-FPM, or database response time.',
      requestId
    );
  }

  if (!error?.response) {
    return appendRequestId(
      'Unable to reach the server. Check Nginx, SSL, CORS, or network access.',
      requestId
    );
  }

  if (error.response?.data?.message) {
    return appendRequestId(error.response.data.message, requestId);
  }

  return appendRequestId(fallbackMessage, requestId);
}

export async function registerRequest(payload) {
  const response = await apiClient.post('/auth/register', payload);
  return response.data;
}

export async function loginRequest(payload) {
  const response = await apiClient.post('/auth/login', payload);
  return response.data;
}

export async function meRequest(accessToken) {
  const response = await apiClient.get('/auth/me', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return response.data;
}

export async function refreshRequest(refreshToken) {
  const response = await apiClient.post('/auth/refresh', {
    refresh_token: refreshToken,
  });

  return response.data;
}

export async function logoutRequest(accessToken) {
  const response = await apiClient.post(
    '/auth/logout',
    {},
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return response.data;
}

export async function dashboardSummaryRequest(accessToken) {
  const response = await apiClient.get('/dashboard/summary', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return response.data;
}

export async function listSessionsRequest(accessToken) {
  const response = await apiClient.get('/auth/sessions', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return response.data;
}

export async function revokeSessionRequest(accessToken, sessionId) {
  const response = await apiClient.delete(`/auth/sessions/${encodeURIComponent(sessionId)}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return response.data;
}

export async function logoutAllRequest(accessToken) {
  const response = await apiClient.post(
    '/auth/logout-all',
    {},
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return response.data;
}

export async function changePasswordRequest(accessToken, payload) {
  const response = await apiClient.post('/auth/change-password', payload, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return response.data;
}
